import { useMemo, useState } from "react";
import { Coins, TrendingDown, Calendar, Utensils, Sparkles } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Spark } from "../ui/decor";
import { Slider } from "../ui/Slider";
import { useCountUp } from "../../hooks/useCountUp";
import { weeklyEstimate } from "../../content/earnings";

/** Common goals for the quick-pick chips (kg). */
const QUICK_PICKS = [3, 5, 10];

/** A meal is ~600 cal: the plain-English translation of the total. */
const CAL_PER_MEAL = 600;

/** One animated stream readout in the results panel. */
function StreamRow({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent: "teal" | "violet";
}) {
  const animated = useCountUp(value);
  const accentText = accent === "teal" ? "text-teal" : "text-violet";
  // Lighter tints for the readout so it stays legible on the navy panel.
  const valueText = accent === "teal" ? "text-teal-light" : "text-violet-light";
  const accentBg = accent === "teal" ? "bg-teal-mist" : "bg-violet-mist";
  return (
    <div className="flex items-center gap-3">
      <span
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-navy ${accentBg} ${accentText}`}
      >
        {icon}
      </span>
      <span className="text-sm font-semibold text-white/90">{label}</span>
      <span className={`num ml-auto text-lg font-extrabold ${valueText}`}>
        +{Math.round(animated).toLocaleString()}
      </span>
    </div>
  );
}

export function BankCalculator() {
  const [targetKg, setTargetKg] = useState(5);
  const [weighIns, setWeighIns] = useState(4);

  // Model: a sensible ~0.5 kg/week pace sets the timeline, then we bank a week
  // at a time across the whole journey.
  const { streams, weeks, total, meals } = useMemo(() => {
    const wks = Math.max(1, Math.ceil(targetKg / 0.5));
    const weeklyKg = targetKg / wks;
    const week = weeklyEstimate(weeklyKg, weighIns);
    // Scale each stream across the whole journey so every readout responds to
    // both sliders (loss drives the two weight streams; weigh-ins drive time).
    const journey = {
      allTime: Math.round(week.allTime * wks),
      threeDay: Math.round(week.threeDay * wks),
      time: Math.round(week.time * wks),
    };
    const journeyTotal = journey.allTime + journey.threeDay + journey.time;
    return {
      streams: journey,
      weeks: wks,
      total: journeyTotal,
      meals: Math.round(journeyTotal / CAL_PER_MEAL),
    };
  }, [targetKg, weighIns]);

  const animatedTotal = useCountUp(total);

  return (
    <section id="calculator" className="relative bg-cream py-20 sm:py-28">
      {/* Playful spark accents (no coloured band, so no Wave). */}
      <Spark className="absolute left-[6%] top-24 hidden h-10 w-10 rotate-12 opacity-80 sm:block" />
      <Spark
        fill="#14B8A6"
        className="absolute right-[8%] top-40 hidden h-7 w-7 -rotate-6 opacity-80 lg:block"
      />
      <Spark
        fill="#8B5CF6"
        className="absolute bottom-16 left-[12%] hidden h-8 w-8 rotate-6 opacity-70 lg:block"
      />

      <div className="container-x">
        <SectionHeading
          eyebrow="See what it banks"
          title={
            <>
              Do the maths on{" "}
              <span className="marker">your own goal</span>
            </>
          }
          sub="Drag the sliders to your goal and pace and watch the bank fill up in real time. It's your progress, translated into a number you can feel."
        />

        <div className="mx-auto mt-14 grid max-w-5xl items-stretch gap-8 lg:grid-cols-2">
          {/* LEFT: the plan */}
          <Reveal>
            <div className="card-pop h-full rotate-[0.5deg] p-6 sm:p-8">
              <h3 className="display text-2xl text-navy">Your plan</h3>
              <p className="mt-1 text-sm text-charcoal/70">
                Set where you're headed and how often you'll step on the scale.
              </p>

              <div className="mt-8 space-y-9">
                <div>
                  <Slider
                    value={targetKg}
                    min={1}
                    max={20}
                    step={0.5}
                    onChange={setTargetKg}
                    accent="teal"
                    label="How much you want to lose"
                    ariaLabel="How much you want to lose, in kilograms"
                    format={(v) => `${v} kg`}
                  />
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-charcoal/55">
                      Quick pick:
                    </span>
                    {QUICK_PICKS.map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setTargetKg(g)}
                        className={`chip cursor-pointer transition-colors ${
                          targetKg === g
                            ? "bg-navy text-white"
                            : "hover:bg-cream-deep"
                        }`}
                        aria-pressed={targetKg === g}
                      >
                        {g} kg
                      </button>
                    ))}
                  </div>
                </div>

                <Slider
                  value={weighIns}
                  min={1}
                  max={7}
                  step={1}
                  onChange={setWeighIns}
                  accent="violet"
                  label="Weigh-ins per week"
                  ariaLabel="Weigh-ins per week"
                  format={(v) => `${v}/wk`}
                />
              </div>

              <div className="mt-8 flex items-center gap-3 rounded-2xl border-2 border-navy bg-sun-pale px-4 py-3">
                <Calendar className="h-5 w-5 shrink-0 text-navy" aria-hidden />
                <p className="text-sm font-semibold text-navy">
                  A gentle{" "}
                  <span className="num font-extrabold">{weeks}</span>-week journey
                  at about half a kilo a week.
                </p>
              </div>
            </div>
          </Reveal>

          {/* RIGHT: what you'd bank */}
          <Reveal delay={1}>
            <div className="card-pop h-full -rotate-[0.5deg] bg-navy p-6 text-white sm:p-8">
              <h3 className="display text-2xl text-white">What you'd bank</h3>
              <p className="mt-1 text-sm text-white/70">
                Three streams stack up across every weigh-in of your journey.
              </p>

              {/* Whole-journey breakdown by stream */}
              <div className="mt-7 space-y-4 rounded-2xl border-2 border-white/85 bg-navy-800 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">
                  Across your whole journey
                </p>
                <StreamRow
                  icon={<TrendingDown className="h-5 w-5" />}
                  label="All-time low"
                  value={streams.allTime}
                  accent="teal"
                />
                <StreamRow
                  icon={<Coins className="h-5 w-5" />}
                  label="Three-day average"
                  value={streams.threeDay}
                  accent="violet"
                />
                <StreamRow
                  icon={<Calendar className="h-5 w-5" />}
                  label="Just for showing up"
                  value={streams.time}
                  accent="teal"
                />
              </div>

              {/* Whole-journey total: the prominent readout */}
              <div className="mt-6 rounded-2xl border-[3px] border-sun bg-navy-950 p-6 text-center shadow-pop-sun">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                  Banked over your whole journey
                </p>
                <p className="num mt-2 text-5xl font-extrabold text-reward sm:text-6xl">
                  {Math.round(animatedTotal).toLocaleString()}
                </p>
                <p className="mt-1 text-sm font-semibold text-white/70">
                  calories earned back, all yours
                </p>
              </div>

              {/* Plain-English translation */}
              <div className="mt-5 flex items-center gap-3 rounded-2xl border-2 border-white/85 bg-teal-deep px-4 py-3">
                <Utensils className="h-5 w-5 shrink-0 text-sun" aria-hidden />
                <p className="text-sm font-semibold text-white">
                  That's roughly{" "}
                  <span className="num font-extrabold text-sun">{meals}</span>{" "}
                  extra meals worth of good, earned progress. Enjoy them without a
                  second thought.
                </p>
              </div>

              <p className="mt-5 flex items-center gap-1.5 text-xs font-medium text-white/60">
                <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                A friendly estimate: your real bank grows with your own weigh-ins.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
